import React from 'react';
import './FeaturesRedesign.css';

const QUICK_NAV = [
  { id: 'generator', label: 'AI Site Generator', color: 'var(--f-accent)' },
  { id: 'editor', label: 'Intuitive Editor', color: 'var(--f-teal)' },
  { id: 'remix', label: 'One-Click Remix', color: 'var(--f-lavender)' },
  { id: 'payments', label: 'Payments', color: 'var(--f-accent)' },
  { id: 'analytics', label: 'Analytics', color: 'var(--f-teal)' },
  { id: 'collaborate', label: 'Multiplayer', color: 'var(--f-lavender)' },
  { id: 'domains', label: 'Domain Registration', color: 'var(--f-accent)' },
  { id: 'storage', label: 'Storage', color: 'var(--f-teal)' },
  { id: 'email', label: 'Email Hosting', color: 'var(--f-lavender)' },
  { id: 'traffic', label: 'Boost Traffic', color: 'var(--f-accent)' },
];

const FEATURE_SECTIONS = [
  {
    id: 'generator',
    title: 'Describe it. Watch it build itself.',
    copy: "Skip the blank page entirely. Type a few words about what you're building and watch a real homepage generate section by section, right in front of you - not a wizard, not a quiz, your actual site taking shape as you type.",
    kicker: 'AI Site Generator',
    color: 'accent',
    isNew: true,
    reverse: false,
    link: '/signup',
    visual: 'generator',
  },
  {
    id: 'editor',
    title: 'Intuitive Website Editor',
    copy: 'Your gateway to designing your brand on the internet. Click the exact word, image, or button you want to change - no separate edit mode, no canvas to fight, no settings buried three menus deep.',
    kicker: 'Editor',
    color: 'teal',
    reverse: true,
    link: '/webbuilder',
    visual: 'editor',
  },
  {
    id: 'remix',
    title: 'Do not love it? Remix it.',
    copy: 'Every generated site comes with infinite variations built in. Hit remix and get a new color palette, font pairing, and layout in seconds - keep exploring until something actually clicks, without losing your content.',
    kicker: 'One-Click Remix',
    color: 'lavender',
    isNew: true,
    reverse: false,
    link: '/signup',
    visual: 'remix',
  },
  {
    id: 'payments',
    title: 'Get paid, built in.',
    copy: 'Checkout, invoicing, and payouts are part of the platform, not a bolt-on plugin you have to configure. Start on the Free plan and upgrade to Growth, Scale, or Enterprise when your business needs more.',
    kicker: 'Payments',
    color: 'accent',
    isNew: true,
    reverse: true,
    link: '/onlinestore',
    visual: 'payments',
  },
  {
    id: 'analytics',
    title: 'Website Analytics',
    copy: 'The best tools to identify and grow your audience. Straightforward insights that require no guesswork - see what is working and drop what is not, without hiring a data analyst.',
    kicker: 'Analytics',
    color: 'teal',
    reverse: false,
    link: '/dashboard',
    visual: 'analytics',
  },
  {
    id: 'collaborate',
    title: 'Build together, live.',
    copy: 'Invite a co-founder, designer, or client into the same canvas and watch their changes appear in real time - like a doc, but it is your homepage.',
    kicker: 'Multiplayer',
    color: 'lavender',
    isNew: true,
    reverse: true,
    link: '/support',
    visual: 'collaborate',
  },
  {
    id: 'domains',
    title: 'Easy Domain Registration',
    copy: 'Need a domain? Search and claim a name in the exact same flow you build your site in - see availability, pricing, and grab it without a separate registrar step.',
    kicker: 'Domain Registration',
    color: 'accent',
    reverse: false,
    link: '/domain',
    visual: 'domains',
  },
  {
    id: 'storage',
    title: 'Website Storage',
    copy: 'All the storage you need with a clear upgrade path across Free, Growth, Scale, and Enterprise. We make it easy to manage and move your data between plans.',
    kicker: 'Storage',
    color: 'teal',
    reverse: true,
    link: '/storage',
    visual: 'storage',
  },
  {
    id: 'email',
    title: 'Email Hosting',
    copy: 'Safe, secure, and simple setup - spin up a professional @yourbrand.com inbox in the same place you built your site, and try it before you commit.',
    kicker: 'Hosting',
    color: 'lavender',
    reverse: false,
    link: '/emailhosting',
    visual: 'email',
  },
  {
    id: 'traffic',
    title: 'Boost Traffic',
    copy: 'Drive the right kind of traffic to your website with built-in SEO checks and campaign tools that are easy to run and easy to measure.',
    kicker: 'Booster',
    color: 'accent',
    reverse: true,
    link: '/marketing',
    visual: 'traffic',
  },
];

const renderVisual = (type) => {
  if (type === 'generator') {
    return (
      <div className="fv-card">
        <div className="fv-bar"><span className="fv-dot" /><span className="fv-dot" /><span className="fv-dot" /></div>
        <div className="fv-body">
          <div className="fv-prompt">
            <span>A cozy bakery site with warm colors...<span className="typing-cursor" /></span>
            <span className="fv-spark-btn">Generate</span>
          </div>
          <div className="fv-gen-grid">
            <div className="fv-gen-block fv-skel" />
            <div className="fv-gen-block fv-skel" />
          </div>
          <div className="fv-row-line fv-skel w70 mt14" />
          <div className="fv-row-line fv-skel w45" />
          <div className="fv-caption"><span className="avail-dot" /> Generated in 1.8s - click anything to edit it</div>
        </div>
      </div>
    );
  }

  if (type === 'editor') {
    return (
      <div className="fv-card">
        <div className="fv-bar"><span className="fv-dot" /><span className="fv-dot" /><span className="fv-dot" /></div>
        <div className="fv-body">
          <div className="fv-row-line accent-bar w35" />
          <div className="fv-row-line h18 w90" />
          <div className="fv-row-line w60" />
          <div className="fv-chiprow">
            <span className="fv-chip">Headline 1</span>
            <span className="fv-chip">Space Grotesk</span>
            <span className="fv-chip">Bold - 32px</span>
          </div>
        </div>
      </div>
    );
  }

  if (type === 'remix') {
    return (
      <div className="fv-palette-stack">
        <div className="fv-palette-card p1">
          <div className="swatches"><span className="sw lav" /><span className="sw d1" /><span className="sw d2" /></div>
          <div className="pline w70" /><div className="pline w45" />
        </div>
        <div className="fv-palette-card p2">
          <div className="swatches"><span className="sw teal" /><span className="sw t1" /><span className="sw d2" /></div>
          <div className="pline w65" /><div className="pline w50" />
        </div>
        <div className="fv-palette-card p3">
          <div className="swatches"><span className="sw accent" /><span className="sw a1" /><span className="sw d2" /></div>
          <div className="pline w75" /><div className="pline w40" />
        </div>
      </div>
    );
  }

  if (type === 'payments') {
    return (
      <div className="fv-card">
        <div className="fv-bar"><span className="fv-dot" /><span className="fv-dot" /><span className="fv-dot" /></div>
        <div className="fv-body fv-checkout">
          <div className="fv-row-line w40 h9" />
          <div className="fv-field" />
          <div className="fv-field-row"><div className="fv-field" /><div className="fv-field" /></div>
          <div className="fv-total-row"><span>Total</span><b>$42.00</b></div>
          <div className="fv-pay-btn">Pay now</div>
        </div>
      </div>
    );
  }

  if (type === 'analytics') {
    return (
      <div className="fv-card">
        <div className="fv-bar"><span className="fv-dot" /><span className="fv-dot" /><span className="fv-dot" /></div>
        <div className="fv-body">
          <div className="fv-chart-wrap">
            <svg viewBox="0 0 260 100" width="100%" height="100%" fill="none">
              <polyline points="0,70 30,55 60,60 90,35 120,42 150,20 180,30 210,15 240,25 260,10" className="chart-accent" />
              <polyline points="0,85 30,80 60,75 90,70 120,68 150,60 180,58 210,50 240,48 260,40" className="chart-teal" />
            </svg>
          </div>
          <div className="fv-legend">Desktop 60% • Mobile 25% • Tablet 15%</div>
        </div>
      </div>
    );
  }

  if (type === 'collaborate') {
    return (
      <div className="fv-card">
        <div className="fv-bar"><span className="fv-dot" /><span className="fv-dot" /><span className="fv-dot" /></div>
        <div className="fv-body">
          <div className="fv-avatar-stack"><span className="fv-avatar a">SK</span><span className="fv-avatar b">AM</span><span className="fv-avatar c">JR</span></div>
          <div className="fv-collab-canvas">
            <div className="fv-row-line w55 h16" />
            <div className="fv-row-line w80" />
            <div className="fv-row-line w60" />
            <div className="cursor-tag purple">Sam</div>
            <div className="cursor-tag green">Alex</div>
          </div>
        </div>
      </div>
    );
  }

  if (type === 'domains') {
    return (
      <div className="fv-card">
        <div className="fv-bar"><span className="fv-dot" /><span className="fv-dot" /><span className="fv-dot" /></div>
        <div className="fv-body">
          <div className="fv-list">
            <div className="fv-list-row"><span className="fv-swatch accent" /><span className="fv-label">warmlybakery.com - $12/yr</span><span className="fv-btn-mini">Claim</span></div>
            <div className="fv-list-row"><span className="fv-swatch teal" /><span className="fv-label">warmlybakery.shop - $8/yr</span><span className="fv-btn-mini outline">Claim</span></div>
            <div className="fv-list-row"><span className="fv-swatch lav" /><span className="fv-label">warmlybakery.co - $22/yr</span><span className="fv-btn-mini outline">Claim</span></div>
          </div>
        </div>
      </div>
    );
  }

  if (type === 'storage') {
    return (
      <div className="fv-card">
        <div className="fv-bar"><span className="fv-dot" /><span className="fv-dot" /><span className="fv-dot" /></div>
        <div className="fv-body">
          <div className="fv-storage-row">
            <div className="fv-storage-tier active"><b>5GB</b>Free</div>
            <div className="fv-storage-tier"><b>30GB</b>Growth</div>
            <div className="fv-storage-tier"><b>40GB</b>Scale</div>
          </div>
          <div className="storage-track"><span className="storage-fill" /></div>
          <div className="fv-caption">1.9GB of 5GB used</div>
        </div>
      </div>
    );
  }

  if (type === 'email') {
    return (
      <div className="fv-card">
        <div className="fv-bar"><span className="fv-dot" /><span className="fv-dot" /><span className="fv-dot" /></div>
        <div className="fv-body">
          <div className="fv-list">
            <div className="fv-list-row"><span className="fv-swatch accent txt">JR</span><span className="fv-label">Jamie R. - Order confirmation?</span><span className="fv-meta">9:41 AM</span></div>
            <div className="fv-list-row"><span className="fv-swatch teal txt">AM</span><span className="fv-label">Alex M. - Custom cake inquiry</span><span className="fv-meta">Yesterday</span></div>
            <div className="fv-list-row"><span className="fv-swatch lav txt">SK</span><span className="fv-label">Sam K. - Loved the new site!</span><span className="fv-meta">Mon</span></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fv-card">
      <div className="fv-bar"><span className="fv-dot" /><span className="fv-dot" /><span className="fv-dot" /></div>
      <div className="fv-body">
        <div className="fv-list">
          <div className="fv-list-row"><span className="fv-label">Organic search</span><span className="mini-bar"><span className="mini-fill accent" /></span><span className="fv-btn-mini">Live</span></div>
          <div className="fv-list-row"><span className="fv-label">Social referral</span><span className="mini-bar"><span className="mini-fill teal w45" /></span><span className="fv-btn-mini">Live</span></div>
          <div className="fv-list-row"><span className="fv-label">Email campaign</span><span className="mini-bar"><span className="mini-fill lav w25" /></span><span className="fv-btn-mini outline">Draft</span></div>
        </div>
      </div>
    </div>
  );
};

const FeaturesContent = ({ history }) => {
  const scrollTo = (id) => {
    const target = document.getElementById(id);
    if (!target) {
      return;
    }
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="features-redesign">
      <section className="feat-hero">
        <div className="features-wrap">
          <div className="eyebrow center">Everything you need to earn online</div>
          <h1>One builder. Every tool it takes to launch, run, and grow a site.</h1>
          <p className="sub">From the first blank page to your first sale - generation, editing, commerce, and growth tools, all in one place.</p>
          <div className="pricing-pill mono"><span className="dollar">$</span> Free, Growth, Scale, Enterprise - upgrade anytime <span className="dollar">$</span></div>

          <div className="hero-ctas">
            <button className="features-btn solid" type="button" onClick={() => history.push('/signup')}>Start Building</button>
            <button className="features-btn ghost" type="button" onClick={() => scrollTo('generator')}>See how it works</button>
          </div>

          <div className="quick-nav">
            {QUICK_NAV.map(item => (
              <button key={item.id} className="qn-pill" type="button" onClick={() => scrollTo(item.id)}>
                <span className="qn-dot" style={{ background: item.color }} />
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {FEATURE_SECTIONS.map((item) => (
        <section className="feat-section" id={item.id} key={item.id}>
          <div className="features-wrap">
            <div className={`feat-row ${item.reverse ? 'reverse' : ''}`}>
              <div className="feat-text">
                <div className={`feat-kicker c-${item.color}`}>
                  <span className="feat-kicker-icon">✦</span>
                  {item.kicker}
                  {item.isNew ? <span className="new-tag">NEW</span> : null}
                </div>
                <h2>{item.title}</h2>
                <p>{item.copy}</p>
                <button className="feat-link" type="button" onClick={() => history.push(item.link)}>
                  Learn more
                </button>
              </div>
              <div className={`feat-visual g-${item.color} ${item.color === 'teal' ? 'bracket bracket--teal' : ''} ${item.color === 'lavender' ? 'bracket bracket--lavender' : ''} ${item.color === 'accent' ? 'bracket' : ''}`}>
                {renderVisual(item.visual)}
              </div>
            </div>
          </div>
        </section>
      ))}
    </div>
  );
};

export default FeaturesContent;
